import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { useSession } from '../../lib/auth/session.js';
import { Badge, Empty, ErrorBanner, TableSkeleton, CardHead } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { RowAction } from '../../components/ui/RowAction.js';
import { confirmAction } from '../../components/ui/Confirm.js';
import { relativeTime } from '../../lib/format.js';

/**
 * SIP security: the addresses fail2ban has banned, and the two lists that
 * override it.
 *
 * Seeing a ban, and lifting one, meant SSH and fail2ban-client. The owner asked
 * for it in the console on 14 September 2026, the day the edge started banning
 * phones that still signed in with a bare number.
 *
 * Nothing here talks to fail2ban. The API records what was asked for on the
 * node, and a root timer there carries it out before its next ban snapshot
 * (roles/fail2ban, arcnet-f2b-apply). A change takes up to half a minute to
 * land, and the page shows it as applying until it has.
 */

interface Why { event: string; count: number; users: string[]; detail: string | null; last_at: string }
interface Ban {
  ip: string; jails: string[]; banned_at: string | null; expires_at: string | null;
  why: Why[]; unban_pending: boolean;
}
interface Rule {
  id: string; address: string; note: string | null; expires_at: string | null;
  created_by: string | null; created_at: string;
  status: 'active' | 'applying' | 'error'; error: string | null;
}
interface SipSecurity {
  available: boolean; taken_at: string | null;
  bans: Ban[]; allow: Rule[]; block: Rule[]; infrastructure: string[];
}

const KEY = ['platform', 'sip-security'] as const;

/** What each kind of edge refusal is called on the page. */
const EVENT_LABEL: Record<string, string> = {
  auth_fail: 'wrong password',
  auth_probe: 'unanswered challenges',
  scanner: 'scanner',
  flood: 'flood',
  cps_limit: 'call rate limit',
  call_denied: 'call refused',
};

const jailName = (j: string) => j.replace(/^arcnet-sip-/, '');

const EXPIRY: ReadonlyArray<readonly [string, string]> = [
  ['', 'No expiry'], ['24', 'For 24 hours'], ['168', 'For 7 days'], ['720', 'For 30 days'],
];

export function SipSecurityPage() {
  const can = useSession((s) => s.can);
  const qc = useQueryClient();
  const [find, setFind] = useState('');

  const data = useQuery({
    queryKey: KEY,
    queryFn: () => api.get<SipSecurity>('/v1/platform/sip-security'),
    // The snapshot behind it is taken every 30 seconds.
    refetchInterval: 15_000,
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: KEY });

  const unban = useMutation({
    mutationFn: (ip: string) => api.post('/v1/platform/sip-security/unban', { ip }),
    onSuccess: refresh,
  });
  const allow = useMutation({
    mutationFn: (ip: string) => api.post('/v1/platform/sip-security/rules',
      { kind: 'allow', address: ip, note: 'Allowed from the ban list', expires_hours: null }),
    onSuccess: refresh,
  });

  const needle = find.trim();
  const matches = (address: string) => !needle || address.includes(needle);
  const d = data.data;
  const bans = (d?.bans ?? []).filter((b) => matches(b.ip));

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">SIP security</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            Addresses the edge has banned, and the lists that override it.
          </p>
        </div>
        <div className="arc-row">
          {d?.taken_at && (
            <span className="arc-subtle" style={{ fontSize: 12 }}>updated {relativeTime(d.taken_at)}</span>
          )}
          <div className="arc-search">
            <Icon name="search" size={14} />
            <input className="arc-input" placeholder="Search addresses" value={find}
              onChange={(e) => setFind(e.target.value)} aria-label="Find an address" />
          </div>
        </div>
      </div>

      {data.error && <ErrorBanner error={data.error} />}
      {d && !d.available && (
        <div className="arc-banner arc-banner-warn" style={{ marginBottom: 14 }}>
          <Icon name="warning" size={14} />
          <span>The node's database cannot be reached, so bans and lists cannot be read or changed.</span>
        </div>
      )}

      <div className="arc-card" style={{ marginBottom: 14 }}>
        <CardHead title="Banned now"
          sub={d ? `${d.bans.length} address${d.bans.length === 1 ? '' : 'es'}` : undefined} />
        {!d ? (
          <table className="arc-table"><TableSkeleton rows={4} cols={5} /></table>
        ) : bans.length === 0 ? (
          <Empty icon="check" title={needle ? 'No banned address matches' : 'Nothing is banned'} />
        ) : (
          <div className="arc-table-wrap">
            <table className="arc-table">
              <colgroup>
                <col style={{ width: 150 }} />
                <col />
                <col style={{ width: 150 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 150 }} />
              </colgroup>
              <thead>
                <tr><th>Address</th><th>Why</th><th>Jail</th><th>Until</th><th /></tr>
              </thead>
              <tbody>
                {bans.map((b) => (
                  <tr key={b.ip}>
                    <td className="arc-mono">{b.ip}</td>
                    <td><WhyCell why={b.why} /></td>
                    <td>
                      {b.jails.map(jailName).join(', ')}
                    </td>
                    <td className="arc-muted"
                      title={b.expires_at ? new Date(b.expires_at).toLocaleString('en-ZA') : undefined}>
                      {b.expires_at ? relativeTime(b.expires_at) : '—'}
                    </td>
                    <td className="arc-td-right">
                      {b.unban_pending ? <Badge tone="info">unbanning</Badge> : (
                        <div className="arc-row" style={{ gap: 2, justifyContent: 'flex-end' }}>
                          {can('node:command') && (
                            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                              disabled={unban.isPending} onClick={() => unban.mutate(b.ip)}>
                              Unban
                            </button>
                          )}
                          {/* Unbans it as well: the node lifts a ban on anything it allows. */}
                          {can('node:write') && (
                            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                              title="Unban, and never ban this address again"
                              disabled={allow.isPending} onClick={() => allow.mutate(b.ip)}>
                              Allow
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <RuleCard kind="allow" rules={d?.allow} infrastructure={d?.infrastructure ?? []}
        matches={matches} canWrite={can('node:write')} />
      <RuleCard kind="block" rules={d?.block} infrastructure={[]}
        matches={matches} canWrite={can('node:write')} />
    </>
  );
}

/** Why an address was banned: the refusals the edge logged for it this week, most recent first. */
function WhyCell({ why }: { why: Why[] }) {
  if (why.length === 0) return <span className="arc-subtle">nothing logged this week</span>;
  return (
    <div className="arc-stack" style={{ gap: 2 }}>
      {why.slice(0, 3).map((w) => (
        <span key={w.event} className="arc-truncate" style={{ fontSize: 12.5 }}
          title={w.detail ?? undefined}>
          {EVENT_LABEL[w.event] ?? w.event}
          {w.count > 1 && <span className="arc-subtle"> ×{w.count}</span>}
          {w.users.length > 0 && <span className="arc-subtle"> as {w.users.join(', ')}</span>}
          <span className="arc-subtle"> · {relativeTime(w.last_at)}</span>
        </span>
      ))}
    </div>
  );
}

function RuleStatus({ rule }: { rule: Rule }) {
  if (rule.status === 'error') {
    return (
      <div className="arc-stack" style={{ gap: 2 }}>
        <Badge tone="danger">not applied</Badge>
        {rule.error && <span className="arc-hint" style={{ marginTop: 0 }}>{rule.error}</span>}
      </div>
    );
  }
  return rule.status === 'applying'
    ? <Badge tone="info">applying</Badge>
    : <Badge tone="ok">active</Badge>;
}

/**
 * One list. The two are the same shape and differ in what an entry does, so
 * they are one component rather than two copies that drift.
 */
function RuleCard({ kind, rules, infrastructure, matches, canWrite }: {
  kind: 'allow' | 'block';
  rules: Rule[] | undefined;
  /** Allow entries from the node's configuration: shown, never removable here. */
  infrastructure: string[];
  matches: (address: string) => boolean;
  canWrite: boolean;
}) {
  const qc = useQueryClient();
  const [address, setAddress] = useState('');
  const [note, setNote] = useState('');
  const [hours, setHours] = useState('');
  const isAllow = kind === 'allow';

  const add = useMutation({
    mutationFn: () => api.post<Rule>('/v1/platform/sip-security/rules', {
      kind, address: address.trim(), note: note.trim() || null,
      expires_hours: hours ? Number(hours) : null,
    }),
    onSuccess: () => {
      setAddress(''); setNote(''); setHours('');
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/platform/sip-security/rules/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });

  const shown = (rules ?? []).filter((r) => matches(r.address));
  const infra = infrastructure.filter(matches);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    add.mutate();
  };

  return (
    <div className="arc-card" style={{ marginBottom: 14 }}>
      <CardHead title={isAllow ? 'Allowed' : 'Blocked'}
        sub={isAllow
          ? 'Never banned, whatever they send. An address, or a network up to /22.'
          : 'Dropped at the firewall on every port. One address at a time.'} />

      {canWrite && (
        <form className="arc-toolbar" onSubmit={submit}>
          <input className="arc-input arc-mono" style={{ maxWidth: 190 }} required
            placeholder={isAllow ? '196.216.192.0/24' : '203.0.113.45'}
            value={address} onChange={(e) => setAddress(e.target.value)}
            aria-label={isAllow ? 'Address or network to allow' : 'Address to block'} />
          <input className="arc-input" style={{ flex: 1, minWidth: 160 }} placeholder="Why"
            value={note} onChange={(e) => setNote(e.target.value)} aria-label="Note" />
          <select className="arc-select" style={{ maxWidth: 140 }} value={hours}
            onChange={(e) => setHours(e.target.value)} aria-label="Expiry">
            {EXPIRY.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
          </select>
          <button type="submit" className="arc-btn arc-btn-sm" disabled={add.isPending}>
            <Icon name="plus" size={12} /> {isAllow ? 'Allow' : 'Block'}
          </button>
        </form>
      )}

      {!rules ? (
        <table className="arc-table"><TableSkeleton rows={2} cols={5} /></table>
      ) : shown.length === 0 && infra.length === 0 ? (
        <Empty icon={isAllow ? 'check' : 'close'}
          title={isAllow ? 'Nothing added to the allow list' : 'Nothing blocked'} />
      ) : (
        <div className="arc-table-wrap">
          <table className="arc-table">
            <colgroup>
              <col style={{ width: 170 }} />
              <col />
              <col style={{ width: 200 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 150 }} />
              <col style={{ width: 50 }} />
            </colgroup>
            <thead>
              <tr><th>Address</th><th>Note</th><th>Added</th><th>Expires</th><th>Status</th><th /></tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.id}>
                  <td className="arc-mono">{r.address}</td>
                  <td className="arc-muted">{r.note ?? <span className="arc-subtle">—</span>}</td>
                  <td className="arc-muted" title={new Date(r.created_at).toLocaleString('en-ZA')}>
                    <span className="arc-truncate">
                      {relativeTime(r.created_at)}{r.created_by ? ` · ${r.created_by}` : ''}
                    </span>
                  </td>
                  <td className="arc-muted">
                    {r.expires_at ? relativeTime(r.expires_at) : <span className="arc-subtle">never</span>}
                  </td>
                  <td><RuleStatus rule={r} /></td>
                  <td className="arc-td-right">
                    {canWrite && (
                      <RowAction kind="delete" label={`Remove ${r.address}`}
                        disabled={remove.isPending}
                        onClick={() => {
                          void confirmAction({
                            title: `Remove ${r.address} from the ${isAllow ? 'allow' : 'block'} list?`,
                            action: 'Remove', danger: true,
                          }).then((ok) => { if (ok) remove.mutate(r.id); });
                        }} />
                    )}
                  </td>
                </tr>
              ))}
              {infra.map((a) => (
                <tr key={`infra-${a}`}>
                  <td className="arc-mono">{a}</td>
                  <td colSpan={3} className="arc-subtle">
                    From the node's configuration: an admin address, this host or the push server.
                  </td>
                  <td className="arc-subtle">infrastructure</td>
                  <td />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
